import { useState } from 'react';
import { api, type SalaryGenPreview, type SalaryGenResult } from '../lib/api';
import { ErrorBox, money } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';
import HeldReqBanner from '../components/HeldReqBanner';

/**
 * 명세 생성.
 *
 * ⚠️ **원장을 통째로 다시 쓴다.** 그 달 현행 명세를 전부 내리고 원천에서 다시 만든다
 *    (레거시도 백업 후 물리삭제·재생성이다. 웹은 소프트 삭제라 직전본이 남는다).
 *    되돌리기 어려운 축이라 화면을 3단으로 나눴다: **미리보기 → 확인 → 실행.**
 *    미리보기 없이 바로 실행하는 버튼은 두지 않는다.
 *
 * ⚠️ 과거 달을 다시 만들면 값이 달라진다. 규칙이 틀려서가 아니라 **원장이 생성 시점 스냅샷**이고
 *    그 뒤 원천(미수 상태·배차·재무 행)이 실제로 바뀌기 때문이다. 그래서 이관분이 있는 달은
 *    서버가 기본 거부하고, 여기서 "이관분 덮어쓰기"를 켜야만 넘어간다.
 */

const fmt = (n: number) => n.toLocaleString('ko-KR');

export default function SalaryGenPanel({ driveMon, onDone }: { driveMon: string; onDone: () => void }) {
  const { me } = useMe();
  const canGen = canScreen(me, 'settle');

  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<SalaryGenPreview | null>(null);
  const [result, setResult] = useState<SalaryGenResult | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'run' | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setPreview(null);
    setResult(null);
    setOverwrite(false);
    setError(null);
  }

  async function load() {
    setBusy('preview');
    setError(null);
    setResult(null);
    try {
      setPreview(await api.previewSalaryGen({ driveMon }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPreview(null);
    } finally {
      setBusy(null);
    }
  }

  async function run() {
    setBusy('run');
    setError(null);
    try {
      const r = await api.generateSalary({ driveMon, overwriteMigrated: overwrite || undefined });
      setResult(r);
      setPreview(null);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (!driveMon) {
    return <p className="text-sm text-ink-faint">정산월을 고르면 명세 생성을 쓸 수 있습니다.</p>;
  }

  if (!open) {
    return (
      // 닫힌 상태는 한 줄로(2026-10-15 way 「상단 폭 최소화」) — 설명은 마우스를 올리면.
      <div
        className="flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-1"
        title="정산현황에 올라온 차주·기사에게 원천(재무추가·월정기·할부금·운행료·영업정산·취소)에서 명세를 다시 만듭니다."
      >
        <h2 className="text-sm font-bold">{driveMon} 명세 생성</h2>
        <button
          className="btn-ghost py-0.5 text-xs"
          onClick={() => {
            setOpen(true);
            reset();
          }}
        >
          열기
        </button>
      </div>
    );
  }

  const migrated = preview?.current.migrated ?? 0;
  const blockedByMigrated = migrated > 0 && !overwrite;

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-sm font-bold">{driveMon} 명세 생성</h2>
        <span className="text-xs text-ink-faint">
          현행 명세를 내리고 원천에서 다시 만듭니다. 직전본은 지워지지 않고 남습니다.
        </span>
        <button
          className="btn-ghost ml-auto py-1 text-xs"
          onClick={() => {
            setOpen(false);
            reset();
          }}
        >
          닫기
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      {!canGen && <p className="text-xs text-ink-faint">명세 생성은 정산 권한이 필요합니다.</p>}

      {canGen && !result && (
        <button className="btn-ghost py-1 text-xs" disabled={busy !== null} onClick={load}>
          {busy === 'preview' ? '계산 중…' : preview ? '다시 계산' : '미리보기'}
        </button>
      )}

      {preview && (
        <>
          {/* 🔴 청구 전이라 이번 생성에서 빠지는 영업정산(OPS 선지급 등) — 실행 전에 보이게(2026-10-02 way). */}
          <HeldReqBanner driveMon={driveMon} context="gen" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Box title="지금 원장" tone="plain">
              <Line label="명세 줄" value={`${fmt(preview.current.rows)}줄`} />
              <Line label="지급" value={`${money(preview.current.plus)}원`} />
              <Line label="공제" value={`${money(preview.current.minus)}원`} />
              {migrated > 0 && (
                <Line
                  label="이관분"
                  value={`${fmt(migrated)}줄`}
                  tone="warn"
                  hint="레거시에서 옮겨온 과거 명세"
                />
              )}
            </Box>
            <Box title="다시 만들면" tone="brand">
              <Line label="명세 줄" value={`${fmt(preview.generated.rows)}줄`} />
              <Line label="지급" value={`${money(preview.generated.plus)}원`} />
              <Line label="공제" value={`${money(preview.generated.minus)}원`} />
              <Line
                label="대상"
                value={`차주 ${fmt(preview.targetOwners)} · 직영기사 ${fmt(preview.targetDrivers)}`}
              />
              {preview.excludedNotSalary > 0 && (
                <Line
                  label="제외"
                  value={`${fmt(preview.excludedNotSalary)}명`}
                  tone="warn"
                  hint="정산현황에서 '정산'이 꺼져 있는 행 — 명세를 만들지 않습니다"
                />
              )}
            </Box>
          </div>

          {preview.excludedNotSalary > 0 && (
            <p className="text-xs text-amber-700">
              정산현황에서 ‘정산’이 꺼진 <b>{fmt(preview.excludedNotSalary)}명</b>은 대상에서 빠집니다.
              기존 명세가 있어도 지우지 않고 그대로 둡니다.
            </p>
          )}

          <p className="text-xs text-ink-soft">
            제외 규칙 — 선지급 {preview.excludePreDeposit ? '제외함' : '포함함'} · 현금{' '}
            {preview.excludeCash ? '제외함' : '포함함'}
            {preview.isFinished && (
              <>
                {' '}
                · <b className="text-amber-700">이 달은 기사정산이 마감</b>돼 있어 마감 당시 값을 씁니다
              </>
            )}
          </p>

          {preview.targetOwners + preview.targetDrivers === 0 && (
            <p className="text-sm text-amber-800">
              정산현황에 대상이 없습니다. 명세는 정산현황(<code>result_salary</code>)에 올라온
              차주·기사에게만 만들어집니다 — 먼저 그쪽을 채워야 합니다.
            </p>
          )}

          {migrated > 0 && (
            <label className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
              />
              <span>
                <b>이관분 덮어쓰기</b> — 이 달 원장 {fmt(migrated)}줄은 레거시에서 옮겨온 과거
                명세입니다. 다시 만들면 그 사이 바뀐 원천이 반영되어 금액이 달라집니다. 그래도
                진행하려면 체크하세요.
              </span>
            </label>
          )}

          <button
            className="btn"
            disabled={busy !== null || blockedByMigrated || preview.targetOwners + preview.targetDrivers === 0}
            onClick={run}
          >
            {busy === 'run' ? '생성 중…' : `${driveMon} 명세 ${fmt(preview.generated.rows)}줄 만들기`}
          </button>
        </>
      )}

      {result && (
        <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <div>
            <b>{result.driveMon} 명세를 다시 만들었습니다.</b> 대상 {fmt(result.targets)}
            (차주 {fmt(result.owners)} · 직영기사 {fmt(result.drivers)}) · 내린 줄{' '}
            {fmt(result.cleared)} · 만든 줄 {fmt(result.created)}
          </div>
          <div>
            지급 {money(String(result.plus))}원 · 공제 {money(String(result.minus))}원
          </div>
          {result.failed > 0 && (
            <div className="text-red-700">
              실패 {fmt(result.failed)}건 —{' '}
              {result.failures
                .map((f) => `${f.ownerId ? `차주 ${f.ownerId}` : `기사 ${f.driverId}`}: ${f.error}`)
                .join(' / ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Box({
  title,
  tone,
  children,
}: {
  title: string;
  tone: 'plain' | 'brand';
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        tone === 'brand' ? 'border-brand-200 bg-brand-50' : 'border-line bg-surface'
      }`}
    >
      <div className="mb-2 text-xs font-bold text-ink-soft">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Line({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone?: 'warn';
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-ink-soft">{label}</span>
      <span className={`font-medium ${tone === 'warn' ? 'text-amber-700' : ''}`} title={hint}>
        {value}
      </span>
    </div>
  );
}
